<template>
  <div v-if="isLoading" class="isLoadingDiv">
    <ProgressSpinner />
  </div>
  <div v-else-if="team" class="p-grid p-mt-2 p-text-left">
    <div class="p-col-12 p-text-left">
      <h1 class="title is-1 displayInline">
        <img
          v-if="team.se_id"
          class="se_indicator"
          v-tooltip="`SportsEngine ID: ${team.se_id}`"
          src="/se_logo_small.png"
        />
        <div class="colorBall" v-bind:style="{ background: team.division.color || '#FFFFFF' }" />
        {{ team.name }}
        <span v-if="team.shortcode"> ({{ team.shortcode }})</span>
      </h1>

      <Button
        class="p-button-outlined p-button-lg floatRight"
        :label="$t('Button.edit')"
        @click="goRouter(`/teams/${team.id}/edit`)"
      />
      <Button
        class="p-button-outlined p-button-lg floatRight p-mr-3"
        :label="$t('Button.view-games')"
        @click="goRouter(`/list?team_id=${team.id}`)"
      />
      <div class="p-my-3">
        {{ $t("Team.team-in") }}
        <router-link :to="`/divisions/${team.division.id}`">{{ team.division.name }} </router-link>
      </div>
    </div>
    <div class="p-col-4">
      <div class="paddedCol whiteBg rounded">
        <div class="strongHeader">
          {{ $t("Team.coaches") }}
          <Button
            class="p-button-outlined p-button-sm floatRight"
            :label="team.coaches.length ? $t('Button.edit') : $t('Button.add-coaches')"
            @click="coachDialog = true"
          />
        </div>
        <div class="title is-3">{{ team.coaches.length }}</div>
        <div v-for="coach in team.coaches" :key="coach.id">
          <p>
            <router-link :to="`/people/${coach.person.id}`">{{ coach.person.name }}</router-link>
          </p>
        </div>
      </div>
      <div class="paddedCol whiteBg rounded p-mt-5">
        <div class="strongHeader">
          {{ $t("Team.staff") }}
          <Button
            class="p-button-outlined p-button-sm floatRight"
            :label="team.coaches.length ? $t('Button.edit') : $t('Button.add-staff')"
            @click="staffDialog = true"
          />
        </div>
        <div class="title is-3">{{ team.staff.length }}</div>
        <div v-for="staff in team.staff" :key="staff.id">
          <p>
            <router-link :to="`/people/${staff.person.id}`">{{ staff.person.name }}</router-link>
          </p>
        </div>
      </div>
      <div class="paddedCol whiteBg rounded p-mt-5">
        <div class="strongHeader">
          {{ $t("home-venues") }}
          <Button
            class="p-button-outlined p-button-sm floatRight"
            :label="team.home_venues.length ? $t('Button.edit') : $t('Button.edit')"
            @click="venueDialog = true"
          />
        </div>
        <div class="title is-3">{{ team.home_venues.length }}</div>
        <div v-for="venue in team.home_venues" :key="venue.id">
          <p>
            <router-link :to="`/venues/${venue.id}`">{{ venue.name }}</router-link>
          </p>
        </div>
      </div>
    </div>
    <div class="p-col-4">
      <div class="paddedCol whiteBg rounded">
        <div class="strongHeader">
          {{ $t("players") }}
          <Button
            class="p-button-outlined p-button-sm floatRight"
            :label="team.coaches.length ? $t('Button.edit') : $t('Button.add-staff')"
            @click="playersDialog = true"
          />
        </div>
        <div class="title is-3">{{ team.players.length }}</div>
        <div v-for="player in team.players" :key="player.id">
          <p>
            <router-link :to="`/people/${player.person.id}`">{{ player.person.name }}</router-link>
          </p>
        </div>
      </div>
    </div>
    <div class="p-col-4">
      <div class="paddedCol whiteBg rounded">
        <div class="strongHeader">
          {{ $t("travel-constraints") }}
          <br />
          <Button class="p-button-outlined p-button-sm" :label="$t('Button.edit')" @click="editTravelConstraints" />
        </div>
        <div v-if="venuesWithoutAddresses.length">
          <div style="color: red">{{ $t("heads-up") }}</div>
          <div>
            {{ $t("one-or-more-venues-dont-have-a-valid-address-to-use-travel-constraints") }}
          </div>
          <div v-for="v in venuesWithoutAddresses" :key="v.id">
            <router-link :to="`/venues/${v.id}/edit?return=/teams/${team.id}`">
              <div class="colorBall" v-bind:style="{ background: v.color || '#FFFFFF' }" />
              {{ v.name }}
            </router-link>
          </div>
        </div>
        <div v-if="team.travel_constraints">
          <travel-constraint v-bind:input="team.travel_constraints"></travel-constraint>
        </div>
        <travel-constraint-form
          :team="team"
          :input="team.travel_constraints"
          @refresh="fetchData"
        ></travel-constraint-form>
      </div>
      <div class="paddedCol whiteBg rounded p-mt-5">
        <div class="strongHeader">
          {{ $t("datetime-constraints") }}
          <br />
          <Button
            id="tdbutton"
            class="p-button-outlined p-button-sm"
            :label="team.time_constraints.length ? $t('Button.add') : $t('Button.add')"
            @click="$modal.show('timeconstraint')"
          />
        </div>
        <!-- <div v-if="!team.time_constraints.length">No constraints added</div> -->
        <div v-for="constraint in team.time_constraints" :key="constraint.id">
          <time-constraint :input="constraint" @refresh="fetchData"></time-constraint>
        </div>

        <time-constraint-form :team="team" @refresh="refreshTeam"></time-constraint-form>
      </div>
    </div>

    <Dialog :header="`Create ` + role" :visible.sync="personDialog" :style="{ width: '600px' }" :modal="true">
      <div>
        <PersonForm :role="role" :modal="true" @done="createdPerson" />
      </div>
    </Dialog>

    <Dialog v-if="team" :header="$t('create-coach')" :visible.sync="coachDialog" :style="{ width: '600px' }" :modal="true">
      <div class="modal-wrapper">
        <div class="p-mb-3">
          <p>
            {{ $t("click-a-person-to-add-or-remove-them-as-a-coach-teams") }}
          </p>
        </div>
        <div class="p-mt-3">
          <div class="columns">
            <div class="column">
              <p class="bold">
                {{ $t("eligible-coaches") }}: {{ eligibleCoaches.length }}
                <br />
              </p>

              <div v-if="eligibleCoaches && eligibleCoaches.length > 0">
                <div v-for="person in eligibleCoaches" :key="person.id">
                  <div @click="createTeamPerson(person, 'coach')" class="listOption">
                    {{ person.first }} {{ person.last }}
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <p class="bold">
                {{ $t("team-coaches") }}: {{ team.coaches.length }}
                <br />
              </p>
              <div v-for="coach in team.coaches" :key="coach.id">
                <div class="listOption" @click="removeTeamPerson(coach)">
                  {{ coach.person.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions p-text-center p-mt-3">
          <br />
          <div class="p-my-3">
            <Button
              class="p-button-sm p-button-secondary"
              :label="$t('Button.create-new-coach')"
              @click="openPersonDialog('coach')"
            />
          </div>
          <br /><br /><br />
          <a class="button" @click="coachDialog = false">{{ $t("Button.done") }}</a>
        </div>
      </div>
    </Dialog>

    <Dialog v-if="team" :header="$t('edit-players')" :visible.sync="playersDialog" :style="{ width: '600px' }" :modal="true">
      <div class="modal-wrapper">
        <div class="modal-header">
          <p>{{ $t("click-a-person-to-add-or-remove-them-as-a-player") }}</p>
        </div>
        <div class="modal-content">
          <div class="columns">
            <div class="column">
              <p class="bold">
                {{ $t("eligible-players") }}
                <br />
              </p>
              <div v-if="eligiblePlayers && eligiblePlayers.length > 0">
                <div v-for="person in eligiblePlayers" class="listOption" :key="person.id">
                  <div @click="createTeamPerson(person, 'player')">{{ person.first }} {{ person.last }}</div>
                </div>
              </div>
              <div class="p-my-3">
                <Button
                  class="p-button-sm p-button-secondary"
                  :label="$t('Button.create-new-player')"
                  @click="openPersonDialog('player')"
                />
              </div>
            </div>
            <div class="column">
              <p class="bold">
                {{ $t('team-players') }}
                <br />
              </p>
              <div v-for="player in team.players" :key="player.id">
                <div class="listOption" @click="removeTeamPerson(player)">
                  {{ player.person.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <a class="button" @click="playersDialog = false">{{ $t("Button.done") }}</a>
        </div>
      </div>
    </Dialog>

    <Dialog v-if="team" :header="$t('edit-staff')" :visible.sync="staffDialog" :style="{ width: '600px' }" :modal="true">
      <div class="modal-wrapper">
        <div class="modal-header">
          <p>{{ $t("click-a-person-to-add-or-remove-them-as-a-staff") }}</p>
        </div>
        <div class="modal-content">
          <div class="columns">
            <div class="column">
              <p class="bold">
                {{ $t("eligible-staff") }}
                <br />
              </p>
              <div v-if="eligibleStaff && eligibleStaff.length > 0">
                <div v-for="person in eligibleStaff" :key="person.id" class="listOption">
                  <div @click="createTeamPerson(person, 'staff')">{{ person.first }} {{ person.last }}</div>
                </div>
              </div>
              <div class="p-my-3">
                <Button
                  class="p-button-sm p-button-secondary"
                  :label="$t('Button.create-new-staff')"
                  @click="openPersonDialog('staff')"
                />
              </div>
            </div>
            <div class="column">
              <p class="bold">
                {{ $t("team-staff") }}
                <br />
              </p>
              <div v-for="staff in team.staff" :key="staff.id">
                <div class="listOption" @click="removeTeamPerson(staff)">
                  {{ staff.person.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <a class="button" @click="staffDialog = false">{{ $t("Button.done") }}</a>
        </div>
      </div>
    </Dialog>

    <Dialog v-if="team" :header="$t('edit-venues')" :visible.sync="venueDialog" :style="{ width: '600px' }" :modal="true">
      <div class="modal-wrapper">
        <div class="modal-header">
          <p>{{ $t("click-a-venue-to-add-or-remove-as-home-venue") }}</p>
          <br />
        </div>
        <div class="modal-content">
          <div class="columns">
            <div class="column">
              <p class="bold">
                {{ $t("eligible-venues") }}
                <br />
              </p>
              <div v-if="eligibleVenues && eligibleVenues.length > 0">
                <div v-for="venue in eligibleVenues" :key="venue.id" class="listOption">
                  <div @click="createVenueTeam(venue)">
                    {{ venue.name }}
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <p class="bold">
                {{ $t("home-venues") }}
                <br />
              </p>
              <div v-for="venue_team in team.venue_teams" :key="venue_team.id">
                <div class="listOption" @click="removeVenueTeam(venue_team)">
                  {{ venue_team.venue.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <a class="button" @click="venueDialog = false">{{ $t("Button.done") }}</a>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import api from "@/api";

import TimeConstraint from "@/components/TimeConstraint";
import TimeConstraintForm from "@/components/TimeConstraintForm";
import TravelConstraint from "@/components/TravelConstraint";
import TravelConstraintForm from "@/components/TravelConstraintForm";

export default {
  components: {
    TimeConstraint,
    TravelConstraint,
    TravelConstraintForm,
    TimeConstraintForm,
  },
  // ddddd
  data() {
    return {
      coachDialog: false,
      teamDialog: false,
      playersDialog: false,
      venueDialog: false,
      staffDialog: false,
      team: {},
      isLoading: true,
      personDialog: false,
      role: "",
    };
  },
  computed: {
    venuesWithoutAddresses() {
      var results = [];
      this.currentSeason.venues.forEach(v => {
        if (!v.has_address) {
          results.push(v);
        }
      });
      return results;
    },
    teamItemIDs() {
      var coach_ids = [];
      var staff_ids = [];
      var player_ids = [];
      var venue_ids = [];
      this.team.coaches.forEach(c => {
        coach_ids.push(c.person.id);
      });
      this.team.players.forEach(c => {
        player_ids.push(c.person.id);
      });
      this.team.staff.forEach(c => {
        staff_ids.push(c.person.id);
      });
      this.team.home_venues.forEach(v => {
        venue_ids.push(v.id);
      });
      return {
        coaches: coach_ids,
        players: player_ids,
        staff: staff_ids,
        venues: venue_ids,
      };
    },
    eligibleCoaches() {
      return this.$store.getters.currentSeason.people.filter(el => {
        return el.is_coach == true && this.teamItemIDs.coaches.indexOf(el.id) < 0;
      });
    },
    eligibleVenues() {
      return this.$store.getters.currentSeason.venues.filter(el => {
        return this.teamItemIDs.venues.indexOf(el.id) < 0;
      });
    },
    eligiblePlayers() {
      return this.$store.getters.currentSeason.people.filter(el => {
        return el.is_player == true && this.teamItemIDs.players.indexOf(el.id) < 0;
      });
    },
    eligibleStaff() {
      return this.$store.getters.currentSeason.people.filter(el => {
        return el.is_staff == true && this.teamItemIDs.staff.indexOf(el.id) < 0;
      });
    },
  },
  methods: {
    createdPerson(person) {
      console.log("Created person", person);
      this.createTeamPerson(person, this.role);
      this.personDialog = false;
    },
    createVenueTeam(venue) {
      api
        .createVenueTeam(this.team.id, venue.id)
        .then(response => {
          this.refreshTeam();
          this.makeToast("success", this.$t("Toast.added-venue-name-to-home-venues", [venue.name]));
          this.$store.dispatch("getLeagueInformation");
        })
        .catch(error => {
          this.errors = error.response.data.errors;
        });
    },
    removeVenueTeam(venue_team) {
      console.log("Remove venue team: " + venue_team.id);
      api
        .removeVenueTeam(venue_team)
        .then(response => {
          this.refreshTeam();
          this.makeToast(
            "info",
            this.$t("Toast.remove-venue_team-venue-name-from-home-venues", [venue_team.venue.name]),
          );
          this.$store.dispatch("getLeagueInformation");
        })
        .catch(error => {
          this.errors = error.response.data.errors;
        });
    },
    openPersonDialog(role) {
      this.role = role;
      this.personDialog = true;
    },
    editTimeConstraints() {
      console.log("Edit time constraints");
    },
    editTravelConstraints() {
      this.$modal.show("example");
    },
    refreshTeam() {
      api
        .getTeam(this.$route.params.team_id, this.$store.getters.currentSeason.id)
        .then(response => {
          this.team = response.data;
          console.log("Team refreshed");
        })
        .catch(e => {
          this.makeToast("error", this.$t("Toast.error.could-not-load-team"));
          this.goRouter("/");
        });
    },
    createTeamPerson(person, role) {
      console.log(`Create team person with role ${role} for ${person.name}`);
      api.createTeamPerson(this.team.id, person.id, role).then(response => {
        this.makeToast(
          "success",
          this.$t("Toast.made-person-first-person-last-a-role", [person.first, person.last, role]),
        );
        this.refreshTeam();
      });
    },
    removeTeamPerson(team_person) {
      console.log(`Remove team person with role ${team_person.role} for ${team_person.person.name}`);
      api.removeTeamPerson(team_person).then(response => {
        this.makeToast(
          "info",
          this.$t("Toast.removed-team_person-person-name-as-a-team_person-role", [
            team_person.person.name,
            team_person.role,
          ]),
        );
        this.refreshTeam();
      });
    },
    fetchData() {
      api
        .getTeam(this.$route.params.team_id, this.$store.getters.currentSeason.id)
        .then(response => {
          this.team = response.data;
          console.log("Team", this.team);
          this.isLoading = false;
        })
        .catch(e => {
          this.makeToast("error", this.$t("Toast.error.could-not-load-team"));
          this.goRouter("/");
        });
    },
  },
  created() {
    this.fetchData();
  },
  watch: {
    "$route.params.team_id": function(id) {
      this.isLoading = true;
      this.fetchData();
      window.analytics.page();
    },
  },
  mounted() {
    console.log("TEAM PAGE MOUNTED");
    window.analytics.page();
    // if (!this.isProduction) {
    //   setTimeout(()=> {
    //     this.$modal.show('timeconstraint')
    //   }, 750);
    // }
  },
};
</script>

<style scoped lang="scss">
.modal-content {
  margin: 10px auto;
}
.listOption {
  line-height: 1.5em;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}
</style>
